import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Scale, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/context/AuthContext";

const T = {
  en: {
    title: "Legal Guard & Data Privacy Consent",
    sub: "Read the agreement and check the three boxes to authorize your purchase.",
    scrollHint: "Keep scrolling to read the complete agreement...",
    c1t: "Biometric Data Authorization (KYC)",
    c1: "I explicitly authorize AvatarNet to process my facial scan and device biometrics strictly for identity verification. I understand that AvatarNet does NOT store raw biometric templates on its servers; data is encrypted locally or handled through certified sandbox APIs (WebAuthn/FaceIO) and will never be sold or shared with third parties.",
    c2t: "Physical Interaction & Proximity Disclaimer (Hold Harmless)",
    c2: "I understand that Proximity Mode facilitates automated introductions between AI agents in physical locations. AvatarNet is a software platform and does NOT vet the behavioral history of users beyond identity verification. I assume full responsibility for all real-world interactions. AvatarNet, its founders, and affiliates are held completely harmless from any physical, psychological, legal, or material damages arising from meetings initiated through the platform.",
    c3t: "AI Interaction & Autonomous Dialogue Acknowledgment",
    c3: "I acknowledge that my AI Avatar utilizes Large Language Models (LLMs) to simulate introductory conversations. While protected by data-masking firewalls, I understand that AI systems can generate unexpected text. I release AvatarNet from liability regarding conversational inaccuracies, AI hallucinations, or perceived emotional distress caused by autonomous avatar-to-avatar dialogues.",
    accept: "I Accept and Authorize",
    logged: "Consent logged with cryptographic signature.",
    entity: "Service Provider: Marketing de Inteligencia Artificial S de RL de CV · Legal Representative: Ricardo Zermeño Mejía · Address: Av. Américas 1545, Floors 20, 21 & 22, Col. Providencia, Guadalajara, Jalisco, México. Any dispute, complaint or claim shall be submitted exclusively to the courts of Guadalajara, Jalisco, México.",
  },
  es: {
    title: "Protección Legal y Consentimiento de Privacidad de Datos",
    sub: "Lee el acuerdo y marca las tres casillas para autorizar tu compra.",
    scrollHint: "Sigue desplazándote para leer el acuerdo completo...",
    c1t: "Autorización de Datos Biométricos (KYC)",
    c1: "Autorizo explícitamente a AvatarNet a procesar mi escaneo facial y biometría del dispositivo estrictamente para verificación de identidad. Entiendo que AvatarNet NO almacena plantillas biométricas crudas en sus servidores; los datos se cifran localmente o se gestionan mediante APIs sandbox certificadas (WebAuthn/FaceIO) y nunca serán vendidos ni compartidos con terceros.",
    c2t: "Interacción Física y Descargo de Proximidad (Exención de Responsabilidad)",
    c2: "Entiendo que el Modo Proximidad facilita presentaciones automatizadas entre agentes de IA en ubicaciones físicas. AvatarNet es una plataforma de software y NO investiga el historial conductual de los usuarios más allá de la verificación de identidad. Asumo plena responsabilidad por todas las interacciones en el mundo real. AvatarNet, sus fundadores y afiliados quedan completamente exentos de cualquier daño físico, psicológico, legal o material derivado de encuentros iniciados a través de la plataforma.",
    c3t: "Reconocimiento de Interacción con IA y Diálogo Autónomo",
    c3: "Reconozco que mi Avatar de IA utiliza Modelos de Lenguaje (LLMs) para simular conversaciones introductorias. Aunque está protegido por firewalls de enmascaramiento de datos, entiendo que los sistemas de IA pueden generar texto inesperado. Libero a AvatarNet de responsabilidad por imprecisiones conversacionales, alucinaciones de IA o angustia emocional percibida causada por diálogos autónomos entre avatares.",
    accept: "Acepto y Autorizo",
    logged: "Consentimiento registrado con firma criptográfica.",
    entity: "Prestador del Servicio: Marketing de Inteligencia Artificial S de RL de CV · Representante Legal: Ricardo Zermeño Mejía · Domicilio: Av. Américas 1545, Pisos 20, 21 y 22, Col. Providencia, Guadalajara, Jalisco, México. Cualquier controversia, querella o reclamación se someterá exclusivamente a los tribunales de Guadalajara, Jalisco, México.",
  },
};

export const LegalConsentModal = ({ lang, user, setUser, onDone, onCancel }) => {
  const t = T[lang] || T.en;
  const [checks, setChecks] = useState({ biometric: false, physical: false, ai_dialogue: false });
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const scrolled = true; // checkboxes are ALWAYS selectable (scroll-lock removed)

  const allChecked = checks.biometric && checks.physical && checks.ai_dialogue;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/legal/consent", checks);
      toast.success(t.logged);
      if (setUser) setUser({ ...user, legal_accepted: true });
      if (onDone) onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const clauses = [
    { key: "biometric", title: t.c1t, text: t.c1 },
    { key: "physical", title: t.c2t, text: t.c2 },
    { key: "ai_dialogue", title: t.c3t, text: t.c3 },
  ];

  return (
    <div className="fixed inset-0 z-[200] bg-[#05050A]/95 sm:backdrop-blur-xl flex items-stretch sm:items-center justify-center sm:px-4 sm:py-6" data-testid="legal-consent-modal">
      <div className="w-full sm:max-w-2xl bg-[#0F0F1A] sm:border border-white/10 sm:rounded-3xl p-5 sm:p-8 flex flex-col h-[100dvh] sm:h-auto sm:max-h-[92vh] relative">
        {onCancel && (
          <button onClick={onCancel} data-testid="legal-close-btn" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 border border-white/15 text-white/60 hover:text-white flex items-center justify-center text-lg z-10">×</button>
        )}
        <h1 className="font-heading text-base sm:text-lg font-black text-white flex items-center gap-2 mb-1 shrink-0 pr-10">
          <Scale size={20} className="text-[#D080FF] shrink-0" /> {t.title}
        </h1>
        <p className="text-[11px] text-[#A0A0B0] mb-3 shrink-0">{t.sub}</p>

        <div ref={boxRef} data-testid="legal-scroll-box"
          className="flex-1 min-h-0 overflow-y-auto bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5 space-y-5 mb-3">
          {clauses.map((c, i) => (
            <div key={c.key}>
              <h2 className="text-xs font-bold text-[#00F5FF] uppercase tracking-widest mb-2">{i + 1}. {c.title}</h2>
              <p className="text-[11px] text-white/70 leading-relaxed">{c.text}</p>
            </div>
          ))}
          <p className="text-[10px] text-white/50 leading-relaxed border-t border-white/10 pt-4" data-testid="legal-entity-block">{t.entity}</p>
          <p className="text-[10px] text-white/30 italic">— Terms v1.1 · AVATAR 4 U / AvatarNet —</p>
        </div>

        <div className="space-y-2 mb-3 shrink-0">
          {clauses.map((c) => (
            <label key={c.key} data-testid={`consent-${c.key}`}
              className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                checks[c.key] ? "border-[#00FFAA]/50 bg-[#00FFAA]/5" : "border-white/10 bg-white/5"
              } ${!scrolled ? "opacity-40 pointer-events-none" : ""}`}>
              <input type="checkbox" disabled={!scrolled} checked={checks[c.key]}
                onChange={(e) => setChecks({ ...checks, [c.key]: e.target.checked })}
                className="w-5 h-5 shrink-0 accent-[#00FFAA]" data-testid={`consent-checkbox-${c.key}`} />
              <span className="text-[11px] font-bold text-white/80 leading-tight">{c.title}</span>
            </label>
          ))}
        </div>

        <button data-testid="legal-accept-btn" onClick={accept} disabled={!allChecked || busy}
          className="w-full py-4 rounded-full text-xs font-bold uppercase tracking-[0.2em] bg-gradient-to-r from-[#B026FF] to-[#00F5FF] shadow-[0_0_25px_rgba(176,38,255,0.5)] disabled:opacity-30 disabled:shadow-none flex items-center justify-center gap-2 shrink-0">
          <ShieldCheck size={16} /> {busy ? "..." : t.accept}
        </button>
      </div>
    </div>
  );
};
